// The Markdown an action's hints are written in: paragraphs, bulleted and
// numbered lists, **bold**, *italic*, [links](https://…) and bare https URLs.
// Nothing else. Parsed into a small tree and rendered as React elements, so
// every character of the source reaches the page as text: there is no HTML
// in, no dangerouslySetInnerHTML, and a link goes only to http(s) or mailto.
// Hints come from an action's manifest — a third party's text.

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'strong'; c: Inline[] }
  | { t: 'em'; c: Inline[] }
  | { t: 'link'; href: string; c: Inline[] };

export type Block = { t: 'p'; c: Inline[] } | { t: 'ul'; items: Inline[][] } | { t: 'ol'; items: Inline[][] };

/** The address a link may go to, or null: only http, https and mailto. */
export function safeHref(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const href = raw.trim();
  if (!/^(https?:\/\/|mailto:)/i.test(href)) return null;
  try {
    const u = new URL(href);
    return u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:' ? u.href : null;
  } catch {
    return null;
  }
}

interface Pattern {
  re: RegExp;
  make: (m: RegExpExecArray) => Inline[];
}

const PATTERNS: Pattern[] = [
  {
    re: /\[([^\]\n]+)\]\(([^)\s]+)\)/,
    make: (m) => {
      const href = safeHref(m[2]);
      // A link to anywhere else keeps its words and loses the link.
      return href ? [{ t: 'link', href, c: parseInline(m[1]) }] : parseInline(m[1]);
    },
  },
  { re: /\*\*([^\n]+?)\*\*/, make: (m) => [{ t: 'strong', c: parseInline(m[1]) }] },
  { re: /__([^\n]+?)__/, make: (m) => [{ t: 'strong', c: parseInline(m[1]) }] },
  { re: /(?<![*\w])\*(?![\s*])([^*\n]+?)\*(?!\*)/, make: (m) => [{ t: 'em', c: parseInline(m[1]) }] },
  { re: /(?<![_\w])_(?![\s_])([^_\n]+?)_(?![_\w])/, make: (m) => [{ t: 'em', c: parseInline(m[1]) }] },
  {
    re: /https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]/,
    make: (m) => {
      const href = safeHref(m[0]);
      return [href ? { t: 'link', href, c: [{ t: 'text', v: m[0] }] } : { t: 'text', v: m[0] }];
    },
  },
];

/** One line of text as inline nodes. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let rest = text;
  while (rest) {
    let best: { m: RegExpExecArray; p: Pattern } | null = null;
    for (const p of PATTERNS) {
      const m = p.re.exec(rest);
      if (m && (!best || m.index < best.m.index)) best = { m, p };
    }
    if (!best) {
      push(out, rest);
      break;
    }
    if (best.m.index > 0) push(out, rest.slice(0, best.m.index));
    for (const node of best.p.make(best.m)) {
      if (node.t === 'text') push(out, node.v);
      else out.push(node);
    }
    rest = rest.slice(best.m.index + best.m[0].length);
  }
  return out;
}

function push(out: Inline[], v: string): void {
  const last = out[out.length - 1];
  if (last?.t === 'text') last.v += v;
  else out.push({ t: 'text', v });
}

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBER = /^\s*\d+[.)]\s+(.*)$/;

/** The whole text as blocks: paragraphs (blank lines between), and lists. */
export function parseMarkdown(text: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { t: 'ul' | 'ol'; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length) blocks.push({ t: 'p', c: parseInline(para.join(' ')) });
    para = [];
  };
  const flushList = () => {
    if (list) blocks.push({ t: list.t, items: list.items.map(parseInline) });
    list = null;
  };
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    const b = BULLET.exec(raw);
    const n = b ? null : NUMBER.exec(raw);
    if (!line) {
      flushPara();
      flushList();
    } else if (b || n) {
      flushPara();
      const kind = b ? 'ul' : 'ol';
      if (list && list.t !== kind) flushList();
      if (!list) list = { t: kind, items: [] };
      list.items.push((b ?? n)![1].trim());
    } else if (list && /^\s{2,}/.test(raw)) {
      // An indented line continues the item above it.
      list.items[list.items.length - 1] += ` ${line}`;
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return blocks;
}

function renderInline(nodes: Inline[], key = ''): ReactNode[] {
  return nodes.map((n, i) => {
    const k = `${key}${i}`;
    switch (n.t) {
      case 'text':
        return n.v;
      case 'strong':
        return <strong key={k} className="font-semibold">{renderInline(n.c, `${k}.`)}</strong>;
      case 'em':
        return <em key={k}>{renderInline(n.c, `${k}.`)}</em>;
      case 'link':
        return (
          <a key={k} href={n.href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-80">
            {renderInline(n.c, `${k}.`)}
          </a>
        );
    }
  });
}

/**
 * Markdown as React. `inline` renders the text's first paragraph as a run of
 * text (a tooltip's one line).
 */
export function Markdown({ text, inline = false, className }: { text: string | null | undefined; inline?: boolean; className?: string }) {
  if (!text) return null;
  const blocks = parseMarkdown(text);
  if (inline) {
    const first = blocks[0];
    const nodes = !first ? [] : first.t === 'p' ? first.c : first.items.flatMap((item, i) => (i ? [{ t: 'text', v: '; ' } as Inline, ...item] : item));
    return <span className={className}>{renderInline(nodes)}</span>;
  }
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {blocks.map((b, i) =>
        b.t === 'p' ? (
          <p key={i}>{renderInline(b.c, `${i}.`)}</p>
        ) : b.t === 'ul' ? (
          <ul key={i} className="list-disc space-y-1 pl-4">
            {b.items.map((item, j) => (
              <li key={j}>{renderInline(item, `${i}.${j}.`)}</li>
            ))}
          </ul>
        ) : (
          <ol key={i} className="list-decimal space-y-1 pl-4">
            {b.items.map((item, j) => (
              <li key={j}>{renderInline(item, `${i}.${j}.`)}</li>
            ))}
          </ol>
        ),
      )}
    </div>
  );
}

/** A hint short enough for a tooltip: one line, no list. */
export function isShortHint(text: string | null | undefined): boolean {
  if (!text) return false;
  const t = text.trim();
  return t.length <= 140 && !t.includes('\n');
}
